/**
 * Parse a DD report markdown blob into the structured pieces that drive the
 * cue-drawer UX. Everything below is best-effort: if the LLM deviates from
 * the template, we fall back gracefully so the markdown still renders.
 *
 * Expected report shape (v2 — inverted pyramid, 6 sections):
 *   # DUE DILIGENCE REPORT — <name>
 *   ## 1. EXECUTIVE SUMMARY            (+ OSF/Soros screen line)
 *   ## 2. RISK ASSESSMENT
 *   ## 3. CONCLUSION AND RECOMMENDATION
 *   ## 4. SUMMARY TABLE                (RAG dashboard, 12 rows + OVERALL)
 *   ## 5. PRIORITY CUES                (#### Cue N — cards, priority only)
 *   ## 6. ANNEXURES                    (remaining cue cards, source list, disclaimer)
 *
 * The parser tolerates the legacy 15-section shape as a fallback.
 */

const STATUS_BY_EMOJI = {
    "🟢": "green",
    "🟡": "amber",
    "🔴": "red",
    "🛑": "red",
    "⚪": "insufficient",
};

function findStatus(line) {
    if (!line) return null;
    for (const e of Object.keys(STATUS_BY_EMOJI)) {
        if (line.includes(e)) return STATUS_BY_EMOJI[e];
    }
    return null;
}

function cleanText(s) {
    if (!s) return "";
    return s
        .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])/g, "")
        .replace(/(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "")
        .replace(/\uFFFD/g, "")
        .trim();
}

/** Split the report into { header, sections: { verdict, table, priority, annexure } }.
 *  Sections are detected by title keyword, so the parser survives renumbering
 *  (the current spec is 1..4; earlier spec was 1,4,5,6). Every H2 that starts
 *  with "## <digit>. " is captured and routed to a logical slot by matching
 *  its title against a keyword list.
 */
export function splitBySections(md) {
    const re = /^##\s+(\d+)\.\s+([^\n]+)/gm;
    const matches = [];
    let m;
    while ((m = re.exec(md)) !== null) {
        matches.push({ n: Number(m[1]), title: m[2].trim(), index: m.index });
    }
    if (matches.length === 0) {
        return { header: md, sections: {}, byNumber: {} };
    }
    const header = md.slice(0, matches[0].index);
    const byNumber = {};
    const sections = {};
    for (let i = 0; i < matches.length; i++) {
        const start = matches[i].index;
        const end = i + 1 < matches.length ? matches[i + 1].index : md.length;
        const block = { title: matches[i].title, markdown: md.slice(start, end), n: matches[i].n };
        byNumber[matches[i].n] = block;
        const t = matches[i].title.toUpperCase();
        if (/VERDICT|EXECUTIVE\s+SUMMARY/.test(t)) sections.verdict = block;
        else if (/SUMMARY\s+TABLE|RAG\s+DASHBOARD/.test(t)) sections.table = block;
        else if (/PRIORITY\s+CUES/.test(t)) sections.priority = block;
        else if (/ANNEXURE/.test(t)) sections.annexure = block;
        else if (/RISK\s+ASSESSMENT/.test(t)) sections.risk = block;
        else if (/CONCLUSION/.test(t)) sections.conclusion = block;
    }
    return { header, sections, byNumber };
}

/** Build a citation map from the source list (Sources annexure). */
export function parseAnnexure(md) {
    const out = {};
    // Prefer the new-format "Sources" annexure subheading (4.2 / 6.2 / etc.).
    let start = md.search(/^#{2,5}\s+\d+\.\d+\s+Sources?\b/im);
    if (start < 0) start = md.search(/^#{2,5}\s+\d+\.\d+\s+Source\s+list/im);
    if (start < 0) start = md.search(/^##\s+\d+\.\s+ANNEXURES?/im);
    if (start < 0) start = md.search(/^##\s+15\.?\s+ANNEXURE/im);
    if (start < 0) start = 0;
    const annex = md.slice(start);
    const re = /(?:^|\n)\s*\*{0,2}\[(\d+)\]\*{0,2}[\s:.—–-]*\[([^\]]+)\]\(([^)]+)\)\s*(?:[—–-]\s*([^\n]*))?/g;
    let m;
    while ((m = re.exec(annex)) !== null) {
        const [, n, title, url, desc] = m;
        if (!out[n]) out[n] = { url, title: title.trim(), description: (desc || "").trim() };
    }
    return out;
}

/** Extract the OSF / Soros screen line. */
export function parseOsfScreen(md) {
    const re = /^>?\s*\*\*OSF\s*\/?\s*Soros\s+Screen:?\*\*\s+([🟢🛑🔴🟡⚪])\s*([A-Z\-]+)?\s*[—–-]?\s*([^\n]*)/imu;
    const m = md.match(re);
    if (!m) return null;
    return {
        status: findStatus(m[1]) || "insufficient",
        label: cleanText(m[2] || ""),
        message: cleanText(m[3] || ""),
    };
}

/** Parse the summary-table rows. Row IDs may include 0 (OSF/Soros) and OVERALL. */
export function parseRagDashboard(md) {
    const rows = [];
    const re = /^\|\s*(\d+)\s*\|\s*([^|]+?)\s*\|\s*([^|]+?)\s*\|\s*([^|]+?)\s*\|\s*$/gmu;
    let m;
    while ((m = re.exec(md)) !== null) {
        const dim = cleanText(m[2].replace(/\*/g, ""));
        const statusCell = m[3].trim();
        const finding = cleanText(m[4].replace(/\*/g, ""));
        const status = findStatus(statusCell);
        if (!status) continue;
        rows.push({
            id: Number(m[1]),
            dimension: dim,
            status,
            statusLabel: cleanText(statusCell.replace(/[🟢🟡🔴🛑⚪]/gu, "")),
            finding,
        });
    }
    const overallTableRe = /^\|\s*\*?\*?OVERALL\*?\*?\s*\|\s*([^|]+?)\s*\|\s*([^|]+?)\s*\|/imu;
    const overallBoldRe = /^\*\*OVERALL\*\*\s*\|\s*([^|\n]+?)\s*\|\s*([^\n]+)$/imu;
    const overall = md.match(overallTableRe) || md.match(overallBoldRe);
    if (overall) {
        const statusCell = overall[1].trim();
        const finding = cleanText(overall[2].replace(/\*/g, ""));
        rows.push({
            id: "overall",
            dimension: "OVERALL",
            status: findStatus(statusCell) || "insufficient",
            statusLabel: cleanText(statusCell.replace(/[🟢🟡🔴🛑⚪]/gu, "")),
            finding,
        });
    }
    return rows;
}

/** Parse "#### Cue N — <dimension> <emoji> <STATUS>" cards from anywhere. */
export function parseCueCards(md) {
    const out = {};
    const re = /^####\s+Cue\s+(\d+)\s+[—–-]\s+([^\n]+?)\s*([🟢🟡🔴🛑⚪])\s*([A-Z\s\-]+)?\s*$/gmu;
    const headings = [];
    let m;
    while ((m = re.exec(md)) !== null) {
        headings.push({
            n: Number(m[1]),
            dimension: m[2].trim(),
            status: findStatus(m[3]) || "insufficient",
            statusLabel: (m[4] || "").trim(),
            start: m.index,
            headerEnd: m.index + m[0].length,
        });
    }
    headings.forEach((h, i) => {
        const next = headings[i + 1] ? headings[i + 1].start : md.length;
        const nextH2 = md.slice(h.headerEnd, next).search(/^##\s+/m);
        const end = nextH2 >= 0 ? h.headerEnd + nextH2 : next;
        const body = md.slice(h.headerEnd, end).trim();
        const fields = parseCardFields(body);
        out[h.n] = {
            n: h.n,
            dimension: cleanText(h.dimension),
            status: h.status,
            statusLabel: cleanText(h.statusLabel),
            ...fields,
        };
    });
    return out;
}

function parseCardFields(body) {
    const grab = (label) => {
        const re = new RegExp(
            `^\\*\\*${label}:?\\*\\*:?\\s*([\\s\\S]*?)(?=\\n\\*\\*[A-Z][\\w \\-/]+:?\\*\\*|$(?![\\r\\n]))`,
            "im"
        );
        const m = body.match(re);
        return m ? m[1].trim() : "";
    };
    const sourcesRaw = grab("Key sources");
    const sourceNums = (sourcesRaw.match(/\[(\d+)\]/g) || []).map((s) => Number(s.slice(1, -1)));
    // Support both new label ("In brief") and legacy ("Plain English").
    const brief = grab("In brief") || grab("Plain English");
    return {
        severity: grab("Severity"),
        flagType: grab("Flag type"),
        plainEnglish: brief,
        brief,
        recommendation: grab("Recommendation"),  // legacy — safe if absent
        pointsToConsider: grab("Points to consider"),
        sourceNums,
        detail: grab("Detail"),
    };
}

/** Split section 1 markdown into overview text + everything after OSF screen. */
function extractOverviewFromSection1(section1Md) {
    if (!section1Md) return "";
    const noHeading = section1Md.replace(/^##\s+1\.[^\n]*\n+/m, "");
    return noHeading.trim();
}

/** Strip the raw markdown table from the "Summary Table" section — we render
 *  it interactively instead. Keep any surrounding narrative. */
function stripTable(md) {
    if (!md) return "";
    // Remove the heading line then any consecutive markdown-table rows
    const noHeading = md.replace(/^##\s+\d+\.[^\n]*\n+/m, "");
    // Drop all table pipe lines and OVERALL bold-pipe line
    const lines = noHeading.split("\n");
    const filtered = [];
    for (const ln of lines) {
        if (/^\s*\|/.test(ln)) continue;
        if (/^\s*\*\*OVERALL\*\*\s*\|/i.test(ln)) continue;
        filtered.push(ln);
    }
    return filtered.join("\n").trim();
}

/** Strip a top-level `## N. TITLE` heading line. */
function stripSectionHeading(md) {
    if (!md) return "";
    return md.replace(/^##\s+\d+\.[^\n]*\n+/m, "").trim();
}

/** Parse the full report into structured pieces we need to render. */
export function parseReport(md) {
    const { header, sections, byNumber } = splitBySections(md);
    // Detect new schema (has Verdict/Summary Table/Annexure headings) vs
    // the legacy 15-section shape.
    const isNew = Boolean(
        sections.verdict || sections.table || sections.annexure
    ) && !(byNumber && byNumber[15]);

    const s1 = sections.verdict?.markdown || "";
    // Legacy Risk Assessment / Conclusion sections may still appear if an old
    // model instance drifts to the previous spec; we keep them optional so
    // rendering degrades gracefully.
    const s2 = sections.risk?.markdown || "";
    const s3 = sections.conclusion?.markdown || "";
    const s4 = sections.table?.markdown || "";
    const s5 = sections.priority?.markdown || "";
    const s6 = sections.annexure?.markdown || "";
    const legacyPost = isNew ? "" : md;

    // Overview = Section 1 (verdict + story + key findings + what to obtain)
    const overview = extractOverviewFromSection1(s1);
    // Section 4 markdown with the raw table stripped (usually just a heading)
    const summaryNarrative = stripTable(s4);
    // Priority cues body without heading
    const priorityBody = stripSectionHeading(s5);
    // Annexures body without heading
    const annexureBody = stripSectionHeading(s6);

    return {
        schemaVersion: isNew ? 2 : 1,
        header,
        // Section 1 pieces
        overview,
        // Section 2 & 3 raw markdown (legacy — rendered as-is only if present)
        riskAssessmentMd: s2,
        conclusionMd: s3,
        // Summary table — replaced with interactive dashboard, extra narrative kept
        summaryNarrativeMd: summaryNarrative,
        // Priority cues (markdown; cards parsed separately)
        priorityCuesMd: priorityBody,
        // Annexures (markdown collapsible)
        annexuresMd: annexureBody,
        // Legacy fallback — everything after section 1 as one blob
        legacyPost,
        // Parsed helpers
        osfScreen: parseOsfScreen(s1 || md),
        ragRows: parseRagDashboard(s4 || md),
        cueCards: parseCueCards(md),
        annexure: parseAnnexure(md),
    };
}
